import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  isPGRST200Error,
  isMissingColumnError,
  normalizePaidFields,
  fetchClientsMap,
} from '@/lib/supabaseResilient';
import {
  OverdueGroup,
  OverdueTransaction,
  OverdueClient,
  OverdueFilters,
  OverdueSummary,
  OverdueStatus,
  OverduePriority,
  ClientStatus,
  RiskLevel,
  calculatePriority,
  calculateRiskLevel
} from '@/types/overdue';
import { Transaction } from '@/types/transaction';
import { differenceInDays, addDays } from 'date-fns';
import { fromDateString } from '@/lib/utils/dateUtils';

export function useOverdueAccounts(filters?: OverdueFilters) {
  const {
    data: transactions = [],
    isLoading: isLoadingTransactions,
    error,
    refetch
  } = useQuery({
    queryKey: ['overdue-transactions'],
    queryFn: async (): Promise<Transaction[]> => {
      console.log('Buscando transações de inadimplência...');

      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError) {
        console.error("Erro ao obter usuário:", userError);
        throw userError;
      }

      if (!user) {
        console.log("Usuário não autenticado.");
        return [];
      }

      // Estratégia resiliente monolab: tenta embedded `clients`; se PGRST200 faz fallback
      // para select simples + join manual. Também trata is_paid vs paid (stub usa `paid`).
      const fetchWithPaidColumn = async (paidCol: 'is_paid' | 'paid') => {
        return supabase
          .from('transactions')
          .select(`
          *,
          clients (
            id,
            name,
            email,
            phone,
            document_number
          )
        `)
          .eq('user_id', user.id)
          .eq('type', 'income')
          // @ts-ignore - coluna pode ser paid ou is_paid conforme ambiente
          .eq(paidCol as any, false)
          .lt('due_date', 'now()')
          .order('due_date', { ascending: false });
      };

      let data: any[] | null = null;
      let error: any = null;
      {
        const resultEmbeddedIsPaid = await fetchWithPaidColumn('is_paid');
        data = resultEmbeddedIsPaid.data as any;
        error = resultEmbeddedIsPaid.error;
        // Fallback is_paid -> paid quando coluna não existe no stub
        if (error && isMissingColumnError(error, 'is_paid')) {
          console.warn('[useOverdueAccounts] is_paid inexistente, tentando com paid');
          const retryPaid = await fetchWithPaidColumn('paid');
          data = retryPaid.data as any;
          error = retryPaid.error;
        }
        // Fallback PGRST200 (FK clients ausente) -> select simples + join manual
        if (error && isPGRST200Error(error)) {
          console.warn('[useOverdueAccounts] PGRST200 em clients embedded, usando fallback sem join', error.message);
          const fetchSimple = async (paidCol2: 'is_paid' | 'paid') =>
            supabase
              .from('transactions')
              .select('*')
              .eq('user_id', user.id)
              .eq('type', 'income')
              // @ts-ignore
              .eq(paidCol2 as any, false)
              .lt('due_date', 'now()')
              .order('due_date', { ascending: false });

          let simpleData: any[] | null = null;
          let simpleError: any = null;
          {
            const r1 = await fetchSimple('is_paid');
            simpleData = r1.data as any;
            simpleError = r1.error;
            if (simpleError && isMissingColumnError(simpleError, 'is_paid')) {
              const r2 = await fetchSimple('paid');
              simpleData = r2.data as any;
              simpleError = r2.error;
            }
          }
          if (simpleError) {
            error = simpleError;
            data = null;
          } else {
            // normaliza paid -> is_paid
            const normalized = normalizePaidFields((simpleData ?? []) as any[]);
            // enriquece com clients manualmente
            const clientIds = normalized.map((r: any) => r.client_id).filter(Boolean);
            const clientsMap = await fetchClientsMap(clientIds, user.id);
            data = normalized.map((row: any) => ({
              ...row,
              clients: row.client_id ? (clientsMap.get(row.client_id) ?? null) : null,
            }));
            error = null;
          }
        }
      }

      if (error) {
        console.error("Erro ao buscar transações:", error);
        throw error;
      }

      // normaliza paid->is_paid também no caminho feliz embedded
      const normalizedData = normalizePaidFields((data ?? []) as any[]);

      console.log(`Encontradas ${normalizedData?.length || 0} transações de inadimplência`);

      return (normalizedData || []).map(transaction => ({
        ...transaction,
        date: fromDateString(transaction.date),
        due_date: fromDateString(transaction.due_date)
      })) as Transaction[];
    },
  });

  // Transformar transações em grupos de inadimplência
  const overdueGroups = useMemo((): OverdueGroup[] => {
    if (!transactions.length) return [];

    // Agrupar transações por cliente
    const transactionsByClient = new Map<string, Transaction[]>();

    transactions.forEach(transaction => {
      // Priorizar o nome do cliente da tabela clients, senão usar o campo client
      const clientName = transaction.clients?.name || transaction.client || 'Cliente não identificado';
      if (!transactionsByClient.has(clientName)) {
        transactionsByClient.set(clientName, []);
      }
      transactionsByClient.get(clientName)!.push(transaction);
    });

    const groups: OverdueGroup[] = [];

    transactionsByClient.forEach((clientTransactions, clientName) => {
      // Obter dados do cliente da primeira transação (todas terão o mesmo cliente)
      const firstTransaction = clientTransactions[0];
      const clientData = firstTransaction.clients;

      // Transformar transações em OverdueTransaction
      const overdueTransactions: OverdueTransaction[] = clientTransactions.map(transaction => {
        const now = new Date();
        const daysOverdue = Math.max(0, differenceInDays(now, transaction.due_date));

        // Calcular juros simples de 1% ao mês sobre o valor original
        const monthlyInterestRate = 0.01;
        const monthsOverdue = daysOverdue / 30;
        const currentAmount = transaction.amount * (1 + (monthlyInterestRate * monthsOverdue));

        return {
          id: transaction.id,
          client_id: clientData?.id || clientName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase(),
          client_name: clientName,
          client_document: clientData?.document_number,
          client_email: clientData?.email,
          client_phone: clientData?.phone,
          description: transaction.description,
          original_amount: transaction.amount,
          current_amount: Number(currentAmount.toFixed(2)),
          due_date: transaction.due_date,
          days_overdue: daysOverdue,
          category_id: transaction.category_id,
          category_name: undefined,
          payment_account_id: transaction.payment_account_id,
          payment_account_name: undefined,
          created_at: new Date(transaction.created_at),
          last_contact: undefined,
          last_contact_type: undefined,
          status: daysOverdue > 90 ? OverdueStatus.OVERDUE : OverdueStatus.IN_PROGRESS,
          priority: calculatePriority(daysOverdue, transaction.amount),
          notes: transaction.notes,
          installment_number: transaction.current_installment,
          total_installments: transaction.total_installments,
          contract_reference: transaction.description
        };
      });

      // Criar OverdueClient
      const totalDebt = overdueTransactions.reduce((sum, t) => sum + t.current_amount, 0);
      const oldestDebtDate = new Date(Math.min(...overdueTransactions.map(t => t.due_date.getTime())));

      // Calcular scoring básico baseado no comportamento de pagamentos passados
      const paymentHistoryScore = 75; // Default médio - será calculado dinamicamente no futuro

      const overdueClient: OverdueClient = {
        id: clientData?.id || clientName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase(),
        name: clientName,
        document: clientData?.document_number,
        email: clientData?.email,
        phone: clientData?.phone,
        total_debt: totalDebt,
        oldest_debt_date: oldestDebtDate,
        total_transactions: overdueTransactions.length,
        last_contact: undefined,
        status: ClientStatus.ACTIVE,
        risk_level: calculateRiskLevel(
          paymentHistoryScore,
          Math.max(...overdueTransactions.map(t => t.days_overdue)),
          totalDebt
        ),
        payment_history_score: paymentHistoryScore
      };

      // Encontrar transações mais antigas e recentes
      const oldestTransaction = overdueTransactions.reduce((oldest, current) =>
        current.days_overdue > oldest.days_overdue ? current : oldest
      );
      const newestTransaction = overdueTransactions.reduce((newest, current) =>
        current.days_overdue < newest.days_overdue ? current : newest
      );
      const averageDaysOverdue = overdueTransactions.reduce((sum, t) => sum + t.days_overdue, 0) / overdueTransactions.length;

      groups.push({
        client: overdueClient,
        transactions: overdueTransactions,
        total_amount: totalDebt,
        oldest_transaction: oldestTransaction,
        newest_transaction: newestTransaction,
        average_days_overdue: averageDaysOverdue
      });
    });

    return groups.sort((a, b) => b.total_amount - a.total_amount);
  }, [transactions]);

  // Aplicar filtros
  const filteredData = useMemo(() => {
    if (!filters) return overdueGroups;

    let filtered = overdueGroups.filter(group => {
      // Status filter
      const hasMatchingStatus = group.transactions.some(t =>
        filters.status.includes(t.status)
      );
      if (!hasMatchingStatus) return false;

      // Priority filter
      const hasMatchingPriority = group.transactions.some(t =>
        filters.priority.includes(t.priority)
      );
      if (!hasMatchingPriority) return false;

      // Search filter
      if (filters.client_search) {
        const searchLower = filters.client_search.toLowerCase();
        if (!group.client.name.toLowerCase().includes(searchLower) &&
            !group.client.document?.toLowerCase().includes(searchLower)) {
          return false;
        }
      }

      // Days overdue filter
      if (filters.days_overdue_min !== undefined || filters.days_overdue_max !== undefined) {
        const minDays = filters.days_overdue_min || 0;
        const maxDays = filters.days_overdue_max || Infinity;
        const hasTransactionInRange = group.transactions.some(t =>
          t.days_overdue >= minDays && t.days_overdue <= maxDays
        );
        if (!hasTransactionInRange) return false;
      }

      // Amount filter
      if (filters.amount_min !== undefined || filters.amount_max !== undefined) {
        const minAmount = filters.amount_min || 0;
        const maxAmount = filters.amount_max || Infinity;
        if (group.total_amount < minAmount || group.total_amount > maxAmount) {
          return false;
        }
      }

      return true;
    });

    // Sort data
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (filters.sort_by) {
        case 'client_name':
          aValue = a.client.name.toLowerCase();
          bValue = b.client.name.toLowerCase();
          break;
        case 'days_overdue':
          aValue = a.oldest_transaction.days_overdue;
          bValue = b.oldest_transaction.days_overdue;
          break;
        case 'amount':
          aValue = a.total_amount;
          bValue = b.total_amount;
          break;
        case 'due_date':
          aValue = a.oldest_transaction.due_date.getTime();
          bValue = b.oldest_transaction.due_date.getTime();
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return filters.sort_direction === 'asc' ? comparison : -comparison;
      } else {
        return filters.sort_direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });

    return filtered;
  }, [overdueGroups, filters]);

  // Calcular resumo
  const summary: OverdueSummary = useMemo(() => {
    const allTransactions = filteredData.flatMap(group => group.transactions);

    const byStatus = Object.values(OverdueStatus).reduce((acc, status) => {
      const statusTransactions = allTransactions.filter(t => t.status === status);
      acc[status] = {
        count: statusTransactions.length,
        amount: statusTransactions.reduce((sum, t) => sum + t.current_amount, 0)
      };
      return acc;
    }, {} as any);

    const byPriority = Object.values(OverduePriority).reduce((acc, priority) => {
      const priorityTransactions = allTransactions.filter(t => t.priority === priority);
      acc[priority] = {
        count: priorityTransactions.length,
        amount: priorityTransactions.reduce((sum, t) => sum + t.current_amount, 0)
      };
      return acc;
    }, {} as any);

    const byAgeRange = {
      '1-30': { count: 0, amount: 0 },
      '31-60': { count: 0, amount: 0 },
      '61-90': { count: 0, amount: 0 },
      '90+': { count: 0, amount: 0 }
    };

    allTransactions.forEach(t => {
      const key = t.days_overdue <= 30 ? '1-30' :
                   t.days_overdue <= 60 ? '31-60' :
                   t.days_overdue <= 90 ? '61-90' : '90+';
      byAgeRange[key].count++;
      byAgeRange[key].amount += t.current_amount;
    });

    return {
      total_overdue_amount: allTransactions.reduce((sum, t) => sum + t.current_amount, 0),
      total_transactions: allTransactions.length,
      total_clients: filteredData.length,
      average_days_overdue: allTransactions.length > 0
        ? allTransactions.reduce((sum, t) => sum + t.days_overdue, 0) / allTransactions.length
        : 0,
      by_status: byStatus,
      by_priority: byPriority,
      by_age_range: byAgeRange
    };
  }, [filteredData]);

  return {
    data: filteredData,
    summary,
    isLoading: isLoadingTransactions,
    error,
    refetch,
    totalTransactions: transactions.length
  };
}